import React, { useState } from 'react';
import { generateDocx } from '../../utils/docxGenerator';
import { useData } from '../../contexts/DataContext';
import { dataService } from '../../services/dataService';
import { EmailService } from '../../services/EmailService';
import SummaryRenderer from './SummaryRenderer';
import { enrichPermitWithLinks } from '../../utils/linkEnricher';
import { getFileName } from '../../utils/imageUtils';

export default function SummaryStep({ value, combinedData, data, currentUser, newPhotoUploaded, onReset, onSubmitSuccess }) {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const { refreshAdminData } = useData();

    const { template_config } = combinedData || {};

    // Default configuration if none exists
    const defaultConfig = {
        spacing: 'normal',
        sections: [
            { id: 'opdracht', label: 'Opdracht', visible: true },
            { id: 'locatie', label: 'Locatie & Object', visible: true },
            { id: 'taak', label: 'Taak & Kwalificatie', visible: true },
            { id: 'personeel', label: 'Personeel', visible: true },
            { id: 'pbms', label: "Benodigde PBM's", visible: true },
            { id: 'gereedschap', label: 'Gereedschap', visible: true },
            { id: 'vergunningen', label: 'Vereiste Werkplannen', visible: true },
            { id: 'loto', label: 'LOTO Procedures', visible: true },
            { id: 'mjop', label: 'NEN 2767 Conditie', visible: true },
            { id: 'risicos', label: "Risico's & Maatregelen", visible: true },
            { id: 'werkstappen', label: 'Werkstappen', visible: true },
            { id: 'handtekeningen', label: 'Handtekeningen', visible: true }
        ]
    };

    let activeConfig = template_config || data?.template_config || defaultConfig;

    // Fallback if config exists but has no sections (e.g. empty object)
    if (!activeConfig.sections || activeConfig.sections.length === 0) {
        console.warn("Invalid config detected, falling back to default");
        activeConfig = defaultConfig;
    }

    // Helper to normalize items (handle both strings and objects)
    const normalizeItems = (items, key) => {
        if (!items) return [];
        return items.map(item => typeof item === 'string' ? { [key]: item } : item);
    };

    // Merge and Deduplicate Tools
    const userTools = normalizeItems(value.tools, 'Tool');
    const uniqueTools = userTools.filter((tool, index, self) =>
        index === self.findIndex(t => t.Tool === tool.Tool)
    );

    // Merge and Deduplicate PBMs
    const userPbms = normalizeItems(value.pbms, 'PBM');
    const uniquePbms = userPbms.filter((pbm, index, self) =>
        index === self.findIndex(p => p.PBM === pbm.PBM)
    );

    // Define fullData early so it can be used in functions
    const fullData = {
        ...value,        // User selection (base data)
        ...combinedData, // Calculated fields (Process logic like LOTO/Permits)
        // CRITICAL: Ensure task images are preserved if not edited by user
        taskImageUrls: value.taskImageUrls && value.taskImageUrls.length > 0
            ? value.taskImageUrls
            : (data?.taskImageUrls || []),
        lotoPhotos: value.lotoPhotos || [], // Explicitly ensure lotoPhotos is passed
        tools: uniqueTools, // Explicitly user selected tools only
        pbms: uniquePbms,   // Explicitly user selected PBMs only
        task_waste_mapping: data?.task_waste_mapping || {},
        // Use the checks that were edited/initialized in Step 2 (HierarchyStep)
        checks: value.checks || []
    };

    // Enrich with Links (manual, electro, technical) for Preview/Render
    // Enrich with Links (manual, electro, technical) for Preview/Render
    // FIX: In Wizard context, 'data' IS the location object, so 'hierarchy' is at the root.
    // In Admin context (if ever used here), it might be different, but SummaryStep is Wizard-specific.
    // We add a fallback just in case data structure changes.
    const locationHierarchy = data?.hierarchy || data?.locations?.[fullData.location]?.hierarchy;
    const enrichedData = enrichPermitWithLinks(fullData, locationHierarchy, data?.onderdeel_tasks);

    // --- Generate Image Metadata for Smart Naming (Live & Submit) ---
    // This ensures that custom component names (from HierarchyStep - pendingAssets)
    // or clean filenames are preserved and visible in the overview AND the final permit.
    const imageMetadata = React.useMemo(() => {
        const fullData = { ...combinedData, ...data }; // Merge for complete view
        const metadata = { ...(fullData.imageMetadata || {}) }; // Initialize with existing from steps

        // Helper to normalize URLs for comparison (ignore query params, decoding)
        const normalize = (u) => {
            if (!u) return '';
            try {
                return decodeURIComponent(u.split('?')[0]);
            } catch (e) { return u; }
        };

        // 1. Capture names from Pending Assets (User defined Component Names - HierarchyStep)
        if (fullData.pendingAssets && fullData.pendingAssets.length > 0) {
            fullData.pendingAssets.forEach(asset => {
                if (asset.instanceData && asset.instanceData.photoUrl) {
                    // Format: "Naam (Type)"
                    const niceName = asset.instanceData.type
                        ? `${asset.instanceData.name} (${asset.instanceData.type})`
                        : asset.instanceData.name;

                    // Store BOTH raw and normalized versions to ensure matching
                    metadata[asset.instanceData.photoUrl] = niceName;
                    metadata[normalize(asset.instanceData.photoUrl)] = niceName;
                }
            });
        }

        // 2. Fallback for other Task Images (use filename if no metadata exists)
        if (fullData.taskImageUrls) {
            fullData.taskImageUrls.forEach(url => {
                // Try exact match OR normalized match
                if (!metadata[url] && !metadata[normalize(url)]) {
                    metadata[url] = getFileName(url);
                }
            });
        }

        // 3. LOTO Photos
        if (fullData.lotoPhotos) {
            fullData.lotoPhotos.forEach(url => {
                if (!metadata[url]) {
                    metadata[url] = getFileName(url);
                }
            });
        }
        return metadata;
    }, [combinedData, data]);

    const submitPermit = async (skipConfirm = false) => {
        if (submitted) return null;

        if (!skipConfirm && !confirm('Weet je zeker dat je dit werkplan wilt indienen ter goedkeuring?')) return null;

        setSubmitting(true);
        try {
            // NEW: Smart Auto-Approval
            const canAutoApprove = await dataService.checkIfApprovedPatternExists(fullData);

            const initialStatus = canAutoApprove ? 'approved' : 'pending';
            const reviewerInfo = initialStatus === 'approved'
                ? { reviewer: 'System (Bekend Patroon)', reviewedAt: new Date().toISOString() }
                : { reviewer: null, reviewedAt: null };

            // CRITICAL FIX: Map assignedUserId (from PersonnelStep) to assignedTo (DB Schema)
            // If assignedTo is already set, keep it, otherwise use fullData.assignedUserId
            const assignedUser = fullData.assignedTo || fullData.assignedUserId;

            // Resolve Supervisor ID (Werkverantwoordelijke) from Name
            let supervisorId = null;
            if (data.users && fullData.werkverantwoordelijke) {
                const supervisor = data.users.find(u =>
                    u.username === fullData.werkverantwoordelijke ||
                    u.name === fullData.werkverantwoordelijke
                );
                if (supervisor) supervisorId = supervisor.uid;
            }



            const dataToSave = {
                ...fullData,
                imageMetadata, // Save the generated metadata
                assignedTo: assignedUser || null, // Ensure explicit null if empty
                supervisorId: supervisorId, // Save the Supervisor UID for dashboards
                template_config: activeConfig,
                applicantId: currentUser?.uid || 'unknown',
                applicantName: currentUser?.username || currentUser?.email || 'Unknown User',
                status: initialStatus,
                feedback: initialStatus === 'approved' ? 'Automatisch goedgekeurd (Bekende foto & taak)' : null,
                ...reviewerInfo
            };



            if (fullData.pendingAssets && fullData.pendingAssets.length > 0) {
            }

            // 0a. DEFERRED: Save Pending Assets (Photos -> Hierarchy)
            if (fullData.pendingAssets && fullData.pendingAssets.length > 0) {
                try {
                    // NEW: Prepare LOTO photos to be attached to the asset instance
                    const preparedLotoPhotos = (fullData.lotoPhotos || []).map(url => ({
                        url,
                        name: fullData.lotoMetadata?.[url]?.tagName || 'LOTO Punt'
                    }));

                    await Promise.all(fullData.pendingAssets.map(asset => {

                        // If this pending asset is the one currently in focus, attach LOTO photos
                        let instanceDataToSave = { ...asset.instanceData };
                        if (asset.instanceData.name === fullData.instanceName) {
                            instanceDataToSave.lotoPhotos = preparedLotoPhotos;
                        }

                        return dataService.addInstanceToHall(
                            asset.location,
                            asset.hall,
                            asset.line,
                            asset.subline,
                            instanceDataToSave
                        );
                    }));
                } catch (assetErr) {
                    console.error("Failed to save pending assets:", assetErr);
                    if (!confirm(`LET OP: Er ging iets mis bij het opslaan van de foto's in de bibliotheek.\n\nFout: ${assetErr.message}\n\nWil je het werkplan toch indienen?`)) {
                        setSubmitting(false);
                        return null;
                    }
                }
            } else if (fullData.instanceName && fullData.lotoPhotos && fullData.lotoPhotos.length > 0) {
                // Case: Existing Asset - Promote LOTO photos to it
                const preparedLotoPhotos = (fullData.lotoPhotos || []).map(url => ({
                    url,
                    name: fullData.lotoMetadata?.[url]?.tagName || 'LOTO Punt'
                }));

                try {
                    await dataService.updateInstanceData(
                        fullData.location || 'Doetinchem',
                        fullData.hall,
                        fullData.line,
                        fullData.subline,
                        fullData.instanceName,
                        { lotoPhotos: preparedLotoPhotos }
                    );
                } catch (promoErr) {
                    console.error("Failed to promote LOTO photos to existing asset:", promoErr);
                    // Non-blocking for the permit itself, but log it.
                }
            }

            // 0b. DEFERRED: Save Task Reference Images (Photos -> Task Library)
            // This ensures these photos appear next time someone selects this task.
            if (fullData.taskImageUrls && fullData.taskImageUrls.length > 0) {
                const locPrefix = fullData.location ? fullData.location.replace(/[^a-zA-Z0-9]/g, '_') : 'Unknown';
                const pathId = [locPrefix, data.hall, data.line, data.subline, data.component, data.task] // Use data or fullData depending on structure
                    .map(s => s ? s.replace(/[^a-zA-Z0-9]/g, '_') : '')
                    .join('_');

                // Re-construct pathId correctly from fullData keys if needed
                const safePathId = [
                    fullData.location || 'Unknown',
                    fullData.hall,
                    fullData.line,
                    fullData.subline,
                    fullData.component,
                    fullData.task
                ].map(s => s ? s.replace(/[^a-zA-Z0-9]/g, '_') : '').join('_');

                try {
                    await dataService.saveTaskImages(safePathId, fullData.taskImageUrls, fullData.location);
                } catch (refErr) {
                    console.error("Failed to save task reference images:", refErr);
                }
            }

            // 0c. DEFERRED: Save LOTO Images (Photos -> LOTO Library)
            // LOTO photos are now only saved upon submission.
            // We fetch existing library, merge with curated list, and save.
            if (fullData.lotoPhotos && fullData.lotoPhotos.length > 0) {
                const locPrefix = fullData.location ? fullData.location.replace(/[^a-zA-Z0-9]/g, '_') : 'Unknown';
                const lotoPathId = `loto_${[
                    locPrefix,
                    fullData.hall,
                    fullData.line,
                    fullData.subline,
                    fullData.component,
                    fullData.task
                ].map(s => s ? s.replace(/[^a-zA-Z0-9]/g, '_') : '').join('_')}`;

                try {

                    // 1. Fetch existing library to avoid overwriting unselected (but valid) history?
                    // Actually, standard behavior for saveTaskImages in other steps is often overwrite or just append locally loaded.
                    // But here we might not have loaded everything.
                    // Safest approach: Fetch current, merge new unique ones, save.
                    const existingUrls = await dataService.getTaskImages(lotoPathId) || [];
                    const combinedUrls = [...new Set([...existingUrls, ...fullData.lotoPhotos])];

                    await dataService.saveTaskImages(lotoPathId, combinedUrls, fullData.location);
                } catch (lotoErr) {
                    console.error("Failed to save LOTO images:", lotoErr);
                    // Non-blocking, but log it.
                }
            }

            // 1. Save to Database
            let savedId;
            try {
                savedId = await dataService.savePermit(dataToSave);
            } catch (saveError) {
                console.error("CRITICAL SAVE ERROR:", saveError);
                alert(`FOUT BIJ OPSLAAN: ${saveError.message}\n\nNeem contact op met beheer.`);
                setSubmitting(false);
                return null;
            }

            // 2. Send Email if not auto-approved
            if (initialStatus !== 'approved') {
                try {
                    // Fetch all users to filter capable WVs or Admins
                    // Note: We use getAllUsers() instead of specific queries to ensure we check specific logic (allowedLocations, email enabled)
                    const allUsers = await dataService.getAllUsers();

                    // Filter WVs for this location
                    let recipients = allUsers.filter(u =>
                        u.role === 'wv' &&
                        u.allowedLocations?.includes(fullData.location) &&
                        u.emailNotificationsEnabled !== false
                    ).map(u => u.username || u.email);

                    // If no WVs found, Fallback to Admins for this location
                    if (!recipients || recipients.length === 0) {
                        recipients = allUsers.filter(u =>
                            u.role === 'admin' &&
                            u.allowedLocations?.includes(fullData.location) &&
                            u.emailNotificationsEnabled !== false
                        ).map(u => u.username || u.email);
                    }

                    if (recipients.length > 0) {
                        // Dedup emails
                        const uniqueRecipients = [...new Set(recipients)];
                        await EmailService.sendSubmissionNotification(dataToSave, uniqueRecipients);
                    } else {
                    }
                } catch (emailErr) {
                    console.error("Failed to send submission email:", emailErr);
                }
            }

            setSubmitted(true);

            // Notify parent to finalize session (clear state so cleanup doesn't delete files)
            if (onSubmitSuccess) {
                onSubmitSuccess();
            }

            // 2. Refresh Admin Data to sync hierarchy/assets immediately
            if (refreshAdminData) {
                refreshAdminData();
            }

            if (initialStatus === 'approved') {
                alert(`Werkplan automatisch goedgekeurd! ✅ (ID: ${savedId})`);
            } else {
                alert(`Werkplan succesvol ingediend! 🚀 (ID: ${savedId})\n\nControleer het Dashboard.`);
            }

            // RELOAD REMOVED PER USER REQUEST - User wants to stay in wizard
            // window.location.reload(); 

            // Return full data with ID
            return { ...dataToSave, id: savedId };
        } catch (error) {
            console.error("Error submitting permit:", error);
            alert('Er is een fout opgetreden: ' + error.message);
            return null;
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = () => submitPermit(false);

    const handlePrint = async () => {
        await submitPermit(true);
        // Wait for state update and then print
        setTimeout(() => window.print(), 500);
    };

    const handleDownload = async () => {
        const savedData = await submitPermit(true);
        // Use savedData if available (has correct status), otherwise fallback to fullData
        generateDocx(savedData || fullData);
    };

    // --- VIEW MODE LOGIC ---
    const [activeView, setActiveView] = useState('document'); // 'document' | 'preview'
    const ExecutionView = React.lazy(() => import('../ExecutionView'));

    return (
        <div className="fade-in space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-8 no-print pb-4 md:pb-0">
                <h2 className="text-2xl font-bold text-white">Stap 7: Overzicht & Printen</h2>

                <div className="flex flex-col w-full md:w-auto md:flex-row gap-3">

                    {submitted ? (
                        <div className="flex gap-2 w-full md:w-auto">
                            <div className="bg-green-600 text-white px-4 py-3 md:py-2 rounded-lg font-bold flex items-center justify-center gap-2">
                                ✓ Ingediend
                            </div>
                            <button
                                onClick={onReset ? onReset : () => window.location.reload()}
                                className="btn btn-secondary bg-slate-700 hover:bg-slate-600 text-white px-4 py-3 md:py-2 rounded-lg flex items-center justify-center gap-2"
                            >
                                🔄 Nieuw Werkplan
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="btn bg-purple-600 hover:bg-purple-700 text-black px-5 py-3 md:py-2.5 rounded-lg flex items-center justify-center gap-2 transition font-black disabled:opacity-50 w-full md:w-auto"
                            style={{ backgroundColor: '#9333ea', color: '#000000' }}
                        >
                            {submitting ? 'Bezig...' : '🚀 Indienen'}
                        </button>
                    )}

                    <div className="flex gap-3 w-full md:w-auto">
                        <button
                            onClick={handleDownload}
                            className="btn bg-blue-600 hover:bg-blue-700 text-black px-5 py-3 md:py-2.5 rounded-lg flex items-center justify-center gap-2 transition font-black flex-1 md:flex-none"
                            style={{ backgroundColor: '#2563eb', color: '#000000' }}
                        >
                            📄 Word
                        </button>
                        <button
                            onClick={handlePrint}
                            className="btn btn-primary flex items-center justify-center gap-2 px-5 py-3 md:py-2.5 text-black font-black flex-1 md:flex-none"
                            style={{ backgroundColor: '#3b82f6', color: '#000000' }}
                        >
                            🖨️ Print
                        </button>
                    </div>
                </div>
            </div>

            {/* VIEW TOGGLE TABS */}
            <div className="flex bg-slate-800 rounded-lg p-1 no-print max-w-md">
                <button
                    onClick={() => setActiveView('document')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-bold transition-all ${activeView === 'document' ? 'shadow-lg' : 'hover:text-white'}`}
                    style={{
                        backgroundColor: activeView === 'document' ? '#2563eb' : 'transparent',
                        color: activeView === 'document' ? '#ffffff' : '#94a3b8'
                    }}
                >
                    📄 Document View
                </button>
                <button
                    onClick={() => setActiveView('preview')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-bold transition-all ${activeView === 'preview' ? 'shadow-lg' : 'hover:text-white'}`}
                    style={{
                        backgroundColor: activeView === 'preview' ? '#2563eb' : 'transparent',
                        color: activeView === 'preview' ? '#ffffff' : '#94a3b8'
                    }}
                >
                    📱 App Preview
                </button>
            </div>

            {activeView === 'preview' ? (
                <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden shadow-2xl h-[800px] relative">
                    <React.Suspense fallback={<div className="flex items-center justify-center h-full text-white">Laden...</div>}>
                        {/* Pass fullData as permit, enable previewMode and embedded */}
                        <ExecutionView
                            permit={{ ...enrichedData, imageMetadata }}
                            previewMode={true}
                            embedded={true}
                            currentUser={currentUser}
                        />
                    </React.Suspense>
                </div>
            ) : (
                <div className="bg-white text-slate-900 w-full p-1 md:p-10 md:card print:shadow-none print:border-none print:p-0 space-y-6 md:space-y-8 overflow-hidden rounded-none md:rounded-xl">
                    {/* Header */}
                    <div className="border-b-4 border-slate-800 pb-6 mb-8 flex flex-col gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-wide break-words">Werkplan - Werkinstructie</h1>
                            <div className="text-sm md:text-base text-slate-600 mt-1">Gegenereerd op {new Date().toLocaleDateString('nl-NL')}</div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <div className="font-bold text-lg md:text-xl">Ultimo: {value.ultimoNr || 'N.v.t.'}</div>
                            <div className="text-sm md:text-base text-slate-600">
                                Datum Uitvoering: {value.date ? new Date(value.date).toLocaleDateString('nl-NL') : 'Niet opgegeven'}
                            </div>
                        </div>
                    </div>

                    {/* Dynamic Content via Renderer */}
                    <SummaryRenderer config={activeConfig} data={{ ...enrichedData, imageMetadata }} />
                </div>
            )}
        </div>
    );
}
